import type { Account, SavingPrices, SizeSaving } from "./api";
import { galText, gallons, STATUS_TEXT } from "./accounts";
import { NO_AREA, tileChange, type TreeTile } from "./sizeHeat";
import type { SortColumn } from "./sort";
import { recommendedCell } from "./workOrders";

export interface SizeRow {
  account: Account;
  recommended: ReturnType<typeof recommendedCell>;
  differs: boolean;
  saving: MonthSaving | null;
}

export const WEEKS_A_MONTH = 52 / 12;

/** A saving as the screens show it. The API gives a week; the payback stays in weeks. */
export type MonthSaving = Omit<SizeSaving, "minutes_week" | "overflows_week"> & { minutes_month: number; overflows_month: number };

export const monthSaving = ({ minutes_week, overflows_week, ...rest }: SizeSaving): MonthSaving => ({
  ...rest,
  minutes_month: minutes_week * WEEKS_A_MONTH,
  overflows_month: overflows_week * WEEKS_A_MONTH,
});

/** SO-1960: a recommended size is a flag only when it changes the container by more than this share. */
export const MATERIAL_CHANGE = 0.25;

/** A recommended total of standard sizes that is another size and more than 25% from the container. */
export function materialChange(recommended: number, capacity: number): boolean {
  if (capacity <= 0) return recommended > 0;
  return recommended !== capacity && Math.abs(recommended - capacity) > MATERIAL_CHANGE * capacity;
}

/** Design 8.6: the container size and the recommended size of each account in service. */
export function sizeRows(accounts: readonly Account[]): SizeRow[] {
  return accounts
    .filter((account) => account.status !== "OFF" && !account.deleted)
    .map((account) => {
      const recommended = recommendedCell(account.projection, account.capacity_gal);
      const sizes = account.projection?.recommended_sizes ?? [];
      const total = account.projection?.recommended_gal ?? sizes.reduce((sum, size) => sum + size, 0);
      const differs = Boolean(recommended && (!sizes.length || materialChange(total, account.capacity_gal)));
      return { account, recommended, differs, saving: account.size_saving ? monthSaving(account.size_saving) : null };
    });
}

export function filterSizes(all: readonly SizeRow[], onlyDiffers: boolean, query: string): SizeRow[] {
  const q = query.trim().toLowerCase();
  return all.filter(
    ({ account, differs }) =>
      (!onlyDiffers || differs) && (!q || `${account.number} ${account.name} ${account.plan?.area ?? ""}`.toLowerCase().includes(q)),
  );
}

/** The accounts of the chosen area, exactly, or the one chosen account. */
export function pickRows(all: readonly SizeRow[], { area, account }: SavingsPick): SizeRow[] {
  if (account !== null) return all.filter((row) => row.account.id === account);
  if (area !== null) return all.filter((row) => (row.account.plan?.area ?? NO_AREA) === area);
  return [...all];
}

export const SIZE_COLUMNS: SortColumn<SizeRow>[] = [
  { key: "account", label: "Account", value: (r) => r.account.number, width: 5.5 },
  { key: "name", label: "Name", value: (r) => r.account.name },
  { key: "area", label: "Area", value: (r) => r.account.plan?.area, width: 5.5 },
  { key: "size", label: "Container size", value: (r) => r.account.capacity_gal, first: "desc", num: true, width: 5.5 },
  { key: "need", label: "Oil each cycle", value: (r) => r.account.projection?.need_gal, first: "desc", num: true, off: true, width: 6 },
  { key: "recommended", label: "Recommended", value: (r) => r.account.projection?.recommended_gal, first: "desc", num: true, width: 8.75 },
  { key: "best", label: "Best size", value: (r) => r.saving?.best_gal, first: "desc", num: true, width: 5.5 },
  { key: "minutes", label: "Minutes saved a month", value: (r) => r.saving?.minutes_month, first: "desc", num: true, width: 5.5 },
  { key: "payback", label: "Payback", value: (r) => r.saving?.payback_weeks, num: true, width: 9 },
  { key: "overflows", label: "Overflows avoided a month", value: (r) => r.saving?.overflows_month, first: "desc", num: true, width: 5.75 },
  { key: "status", label: "Status", value: (r) => r.account.status, order: Object.keys(STATUS_TEXT), off: true, width: 6 },
];

/** One decimal below 10 minutes, because a real saving can be small. */
export function minutesText(minutes: number) {
  const digits = Math.round(minutes * 10) / 10 < 10 ? 1 : 0;
  return minutes.toLocaleString("en-US", { maximumFractionDigits: digits });
}
export const overflowsText = (overflows: number) => overflows.toFixed(2);
export const LONG_PAYBACK_WEEKS = 104;

const WEEKS_A_YEAR = 52;
export const MINIMUM_MARK = "Min";

/** The payback with its unit: weeks below 104 weeks, else years with one decimal. */
export function paybackText(weeks: number | null) {
  if (weeks === null) return "–";
  if (weeks >= LONG_PAYBACK_WEEKS) return `${(weeks / WEEKS_A_YEAR).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} years`;
  const number = weeks.toLocaleString("en-US", { maximumFractionDigits: 1 });
  return `${number} ${number === "1" ? "week" : "weeks"}`;
}

/** The payback in words, with the W-3 minimum spelled out. */
function paybackWords(weeks: number | null, forced: boolean) {
  if (!forced) return `payback ${paybackText(weeks)}`;
  return weeks === null ? "Minimum size" : `Minimum size · payback ${paybackText(weeks)}`;
}

/** True when the container is under the W-3 minimum and the best size is that minimum. */
export function forcedSwap({ account, saving }: SizeRow) {
  const minimum = account.projection?.recommended_gal;
  return saving !== null && minimum != null && account.capacity_gal < minimum && saving.best_gal === minimum;
}

const isSwap = ({ saving }: SizeRow) => (saving?.minutes_month ?? 0) > 0;

/** True when the rows hold a swap and no swap pays back in 2 years. */
export function noQuickPayback(rows: readonly SizeRow[]) {
  const swaps = rows.filter(isSwap);
  return swaps.length > 0 && swaps.every(({ saving }) => saving?.payback_weeks == null || saving.payback_weeks >= LONG_PAYBACK_WEEKS);
}

/** Show the resize link only for a swap to one size that saves minutes and that the user can order. */
export const canResize = (saving: MonthSaving | null) =>
  saving !== null && saving.minutes_month > 0 && saving.standard !== false && saving.can_order !== false;

/** The best size. A pair of sizes shows as the recommended pair. */
export function bestText(saving: MonthSaving, projection: Account["projection"], recommended: string | undefined) {
  const pair = saving.standard === false && recommended !== undefined && saving.best_gal === projection?.recommended_gal;
  return pair ? recommended : galText(saving.best_gal);
}

export const NOTE_TITLE = "How we count the minutes";
export const MAP_MEANING = "On the map, the area of a tile is the gallons of the container, and the colour is the real driver minutes a month that the best size saves. The payback is in the table, on the detail line and on the result line.";
export const SIZE_MEANING = "Recommended: the smallest size that holds one cycle. Best size: the size with the lowest 52-week cost. Min: the best size is the recommended size, the minimum.";

/** What the minutes hold. With no prices from an older API, nothing. */
export function savingNote(prices: SavingPrices | undefined) {
  if (!prices) return "";
  const overflow = "Minutes are real driver minutes: fewer visits, plus one depot round trip and one service for each overflow avoided. The best size also weighs the risk of an overflow, so a swap can have a long payback. A month is 52 ÷ 12 weeks.";
  const tanks = Object.values(prices.tank_min ?? {});
  if (!tanks.length) return `${overflow} Payback is the ${prices.swap_min}-minute swap ÷ the minutes saved a week. The recommended size stays the minimum.`;
  const low = Math.min(...tanks).toLocaleString("en-US");
  const high = Math.max(...tanks).toLocaleString("en-US");
  return `${overflow} Payback is (the ${prices.swap_min}-minute swap + the container price) ÷ the minutes saved a week. A container price is ${low} to ${high} minutes, estimated, not from a quote. The recommended size stays the minimum.`;
}

export const LONG_PAYBACK_LINE = "No swap pays back in 2 years on driver minutes alone.";
export const NO_SAVINGS = "No size savings yet. An account needs an estimate from at least 2 pickups and a recommended size.";

const swapsText = (swaps: number) => `${swaps.toLocaleString("en-US")} ${swaps === 1 ? "swap" : "swaps"}`;

/** The result of one chosen account. */
function accountResult(row: SizeRow, saving: MonthSaving) {
  const { account } = row;
  const head = `${account.number} ${account.name}`;
  if (saving.minutes_month <= 0 && saving.best_gal === account.capacity_gal) return `${head}: ${galText(account.capacity_gal)}, the container is the best size`;
  return `${head}: ${sizeChangeText(row)} · ${minutesText(saving.minutes_month)} min a month · ${overflowsText(saving.overflows_month)} overflows avoided a month · ${paybackWords(saving.payback_weeks, forcedSwap(row))}`;
}

/** The result line of a chosen area, or of one chosen account with `one`. */
export function resultText(label: string, rows: readonly SizeRow[], one = false) {
  if (rows.length > 0 && rows.every((row) => row.saving === null)) return `${label}: no estimate yet`;
  const saving = rows[0]?.saving;
  if (one && saving) return accountResult(rows[0], saving);
  const minutes = rows.reduce((sum, row) => sum + (row.saving?.minutes_month ?? 0), 0);
  const swaps = rows.filter(isSwap).length;
  const count = rows.length.toLocaleString("en-US");
  return `${label}: ${count} ${rows.length === 1 ? "account" : "accounts"}, ${swapsText(swaps)} · ${minutesText(minutes)} min a month`;
}

/** The recommended size change, as "70 → 110 gal". A screen reader gets the word "to" in place of the arrow. */
export function sizeChangeText({ account, recommended, saving }: SizeRow, arrow = "→") {
  return saving ? `${gallons(account.capacity_gal)} ${arrow} ${bestText(saving, account.projection, recommended?.text)}` : "";
}

/** What a treemap tile of one account says, on the detail line and to a screen reader. */
export function tileText(row: SizeRow, arrow = "→") {
  const { account, saving } = row;
  const head = `${account.number} ${account.name}, ${account.plan?.area ?? NO_AREA}`;
  if (!saving) return `${head}: ${galText(account.capacity_gal)}, no estimate yet`;
  if (saving.minutes_month <= 0 && saving.best_gal === account.capacity_gal) return `${head}: ${galText(account.capacity_gal)}, 0 min a month, the container is the best size`;
  return `${head}: ${minutesText(saving.minutes_month)} min a month, best size ${sizeChangeText(row, arrow)}, ${paybackWords(saving.payback_weeks, forcedSwap(row))}, ${overflowsText(saving.overflows_month)} overflows avoided a month`;
}

/** What the Other tile of an area says. */
export const otherTileText = ({ area, accounts, gallons: total, noEstimate }: Pick<TreeTile, "area" | "accounts" | "gallons" | "noEstimate">) =>
  `${area}: Other, ${accounts.toLocaleString("en-US")} ${accounts === 1 ? "account" : "accounts"}, ${galText(total)}, ${noEstimate ? "no estimate yet" : "no minutes saved"}`;

/** The text in a large tile: the size change and the minutes of a saving, else the gallons. */
export function tileLines(tile: TreeTile) {
  if (!tile.row) return { first: `Other · ${tile.accounts.toLocaleString("en-US")}`, second: galText(tile.gallons), change: false };
  if (tile.minutes <= 0) return { first: galText(tile.gallons), second: "", change: false };
  const change = tileChange(tile, sizeChangeText(tile.row));
  return { first: change || galText(tile.gallons), second: `${minutesText(tile.minutes)} min`, change: change !== "" };
}

export const sizeSummary = (shown: number, all: number, differs: number) =>
  `${shown.toLocaleString("en-US")} of ${all.toLocaleString("en-US")} accounts · ${differs.toLocaleString("en-US")} with a recommended size more than 25% different`;

/** "No container" for an account with no container, not "0 gal". */
export const sizeText = (capacity: number) => (capacity > 0 ? galText(capacity) : "No container");

export interface SizesFilter {
  differs: boolean;
  q: string;
}

/** The filter of the Sizes tab from the URL (`?differs=1&q=`), so a reload, Back and a link keep it. */
export function readSizesFilter(search: string): SizesFilter {
  const params = new URLSearchParams(search);
  return { differs: params.get("differs") === "1", q: params.get("q") ?? "" };
}

/** The search with this filter; the other values of the URL stay. */
export function writeSizesFilter(search: string, filter: SizesFilter): string {
  const params = new URLSearchParams(search);
  if (filter.differs) params.set("differs", "1");
  else params.delete("differs");
  if (filter.q) params.set("q", filter.q);
  else params.delete("q");
  const text = params.toString();
  return text ? `?${text}` : "";
}

/** The choice on the Size savings page. An account inside an area keeps the area, so the treemap stays on it. */
export interface SavingsPick {
  area: string | null;
  account: string | null;
}

export function readSavingsPick(search: string): SavingsPick {
  const params = new URLSearchParams(search);
  return { area: params.get("area"), account: params.get("account") };
}

/** The search with this choice; the other values of the URL stay. */
export function writeSavingsPick(search: string, { area, account }: SavingsPick): string {
  const params = new URLSearchParams(search);
  if (area !== null) params.set("area", area);
  else params.delete("area");
  if (account !== null) params.set("account", account);
  else params.delete("account");
  const text = params.toString();
  return text ? `?${text}` : "";
}
